<template>
    <DsTextInput
        :actionableHelperText
        :disabled
        :error
        :helperText
        :label
        :placeholder
        :readonly
        :required
        :size
        :type
        :warning
    >
        <template #input="{ inputClass, error, placeholder, disabled, readonly }">
            <span
                v-if="modelValue"
                class="pointer-events-none absolute font-mono"
                data-testid="mask-value"
            >
                {{ isMasked ? maskedInputValue : modelValue }}
            </span>
            <input
                v-model="modelValue"
                ref="inputElement"
                :class="[
                    'w-full border-none bg-transparent font-mono text-transparent caret-black focus:ring-0',
                    inputClass,
                    { 'has-error': error },
                ]"
                :disabled
                :placeholder
                :readonly
                type="text"
                @blur="handleBlur"
                @focus="isFocused = true"
                @input="handleInput"
            />
        </template>
        <template #trailing>
            <DsIconButton
                :icon="['far', isMasked ? 'fa-eye-slash' : 'fa-eye']"
                size="xs"
                tooltipText="Toggle mask"
                @click.stop="isMasked = !isMasked"
            />
        </template>
    </DsTextInput>
</template>

<script setup lang="ts">
import DsIconButton from '@/components/design-system/button/DsIconButton.vue';
import DsTextInput from '@ds/form/input/text/DsTextInput.vue';
import { formatAccountNumber, formatEIN, formatSSN, getMaskedValue } from '@ds/form/input/text/text-input-mask';
import type { DsTextInputProps } from '@ds/form/input/text/text-input.types';
import { MaskType } from '@ds/form/input/text/text-input.types';
import { computed, ref, useTemplateRef, watch } from 'vue';

interface DsTextInputMaskedProps extends DsTextInputProps {
    mask: MaskType;
    numVisibleCharacters?: number;
}

const props = withDefaults(defineProps<DsTextInputMaskedProps>(), {
    numVisibleCharacters: 4,
});

const modelValue = defineModel<string>({ default: '' });
const inputElement = useTemplateRef('inputElement');
const isMasked = ref(false);

const maskedInputValue = computed(() => {
    switch (props.mask) {
        case MaskType.SSN:
        case MaskType.EIN:
        case MaskType.ACCOUNT_NUMBER:
            return getMaskedValue(modelValue.value, props.mask, props.numVisibleCharacters);
        default:
            return modelValue.value;
    }
});

const formatInputValue = (value: string) => {
    switch (props.mask) {
        case MaskType.SSN:
            return formatSSN(value);
        case MaskType.EIN:
            return formatEIN(value);
        case MaskType.ACCOUNT_NUMBER:
            return formatAccountNumber(value);
        default:
            return value;
    }
};

const handleInput = (event: Event) => {
    const inputValue = (event.target as HTMLInputElement)?.value || '';
    const formattedValue = formatInputValue(inputValue);
    modelValue.value = formattedValue;
};

const handleBlur = (event: FocusEvent) => {
    isFocused.value = false;

    // Check if the blur is happening because user is clicking the toggle button
    const toggleButton = event.relatedTarget as HTMLElement;
    const isClickingToggle = toggleButton && toggleButton.getAttribute('aria-label') === 'Toggle mask';

    // Only mask if user is not clicking the toggle button
    if (!isClickingToggle) {
        isMasked.value = true;
    }
};

const isFocused = defineModel<boolean>('isFocused');
watch(isFocused, value => {
    if (value) {
        inputElement.value?.focus();
    }
});
</script>
